<template>
  <el-dialog
    :title="dialogTitle"
    :visible="visible"
    width="740px"
    append-to-body
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <div v-if="detail" class="detail-container">
      <div class="section-title">基础信息</div>
      <div class="custom-desc">
        <!-- 工具详情弹窗 -->
        <template v-if="mode === 'tool'">
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工具编号</div>
              <div class="desc-value">
                <code class="highlight-code">{{ detail.toolCode || '-' }}</code>
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">状态</div>
              <div class="desc-value">
                <el-tag :type="detail.status === 'available' ? 'success' : 'danger'" size="small" effect="light">
                  {{ detail.status === 'available' ? '可借用' : '已借完' }}
                </el-tag>
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工具名称</div>
              <div class="desc-value">{{ detail.toolName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">工具类型</div>
              <div class="desc-value">
                <dict-tag :dict-type="DICT_TYPE.TOOL_TYPE" :value="detail.toolType" />
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">可用/总数</div>
              <div class="desc-value">
                {{ detail.availableCount }} / {{ detail.totalCount }}
                <span v-if="detail.availableRatio != null" class="ratio-hint">（可用占比 {{ detail.availableRatio }}%）</span>
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">存放位置</div>
              <div class="desc-value">{{ detail.storageLocation || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">创建人</div>
              <div class="desc-value">{{ detail.createUserName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">创建时间</div>
              <div class="desc-value">{{ formatDateTime(detail.createTime) }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item" style="flex: 1">
              <div class="desc-label">备注</div>
              <div class="desc-value desc-value--multiline">{{ detail.remark || '-' }}</div>
            </div>
          </div>
        </template>

        <!-- 借用记录详情弹窗 -->
        <template v-else-if="mode === 'borrow'">
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工具编号</div>
              <div class="desc-value">
                <code class="highlight-code">{{ detail.toolCode || '-' }}</code>
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">借用状态</div>
              <div class="desc-value">
                <el-tag :type="borrowStatusType(detail.status)" size="small" effect="light">
                  {{ borrowStatusLabel(detail.status) }}
                </el-tag>
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工具名称</div>
              <div class="desc-value">{{ detail.toolName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">借用数量</div>
              <div class="desc-value">{{ detail.borrowCount != null ? detail.borrowCount : '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item" style="flex: 1">
              <div class="desc-label">用途说明</div>
              <div class="desc-value desc-value--multiline">{{ detail.purpose || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">借用人</div>
              <div class="desc-value">{{ detail.borrowUserName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">申请时间</div>
              <div class="desc-value">{{ formatDateTime(detail.applyTime) }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">预计归还时间</div>
              <div class="desc-value">{{ formatDateTime(detail.expectReturnTime) }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">审批人</div>
              <div class="desc-value">{{ detail.approveUserName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">实际归还时间</div>
              <div class="desc-value">{{ formatDateTime(detail.returnTime) }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">审批备注</div>
              <div class="desc-value desc-value--multiline">{{ detail.remark || '-' }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { DICT_TYPE } from '@/features/components/dict/dictTypeKeys';

export default {
  name: 'ToolDetailDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 工具台账行或借用记录行 */
    detail: {
      type: Object,
      default: null
    },
    /** tool：工具台账；borrow：借用记录 */
    mode: {
      type: String,
      default: 'tool'
    }
  },
  data() {
    return { DICT_TYPE };
  },
  computed: {
    dialogTitle () {
      return this.mode === 'tool' ? '工具详情' : '借用记录详情';
    },
  },
  methods: {
    formatDateTime,
    borrowStatusType (status) {
      const map = {
        pending: 'warning',
        using: 'primary',
        return_pending: 'warning',
        returned: 'success',
        rejected: 'danger'
      };
      return map[status] || 'info';
    },
    borrowStatusLabel (status) {
      const map = {
        pending: '待审批',
        using: '使用中',
        return_pending: '待确认归还',
        returned: '已归还',
        rejected: '已拒绝'
      };
      return map[status] || status;
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-container {
  padding: 0 4px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}

.custom-desc {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  font-size: 14px;
  background: #fff;
}

.desc-row {
  display: flex;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.desc-item {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-right: 1px solid #ebeef5;

  &:last-child {
    border-right: none;
  }
}

.desc-label {
  width: 110px;
  background-color: #f8f9fb;
  color: #606266;
  padding: 10px 12px;
  box-sizing: border-box;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.desc-value {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  color: #303133;
  box-sizing: border-box;
  word-break: break-all;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.desc-value--multiline {
  white-space: pre-wrap;
  line-height: 1.6;
  align-items: flex-start;
  padding-top: 5px;
  padding-bottom: 5px;
}

.highlight-code {
  color: #409eff;
  background: #ecf5ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
}

.ratio-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}
</style>
